import { getReplay } from "@dailywars/core/db";
import {
  challengeMapIdToNumber,
  isBetaMapId,
  isChallengeMapId,
  isChallengeReplayPublic,
  mapIdToVariant,
  type GameState,
  type Move,
} from "@dailywars/core/engine";
import type { MapVariant } from "@dailywars/core/engine/maps/mapId";
import { toUtcDateString } from "@dailywars/core/entitlements/mapAccess";
import { createServerFn } from "@tanstack/react-start";
import * as z from "zod";

import { GUEST_DISPLAY_NAME } from "#/lib/playerDisplayName";
import { setRouteAction } from "#/lib/requestLogger.server";
import { replayIdSchema } from "#/lib/routeParams";
import { resolveLeaderboardDisplayNames } from "#/server/game/leaderboardPlayers";
import { resolveRequestGameContext } from "#/server/game/requestGameContext.server";

function replayVariant(mapId: string): MapVariant {
  if (isChallengeMapId(mapId) || isBetaMapId(mapId)) return "premium";
  return mapIdToVariant(mapId);
}

const loadReplayInputSchema = z.object({
  replayId: replayIdSchema,
});

export type ReplayPageData = {
  locked: boolean;
  initialState: GameState;
  moves: Move[];
  mapId: string;
  mapDate: string;
  variant: MapVariant;
  playerId: string;
  displayName: string;
  finalServerDayNumber: number;
};

export function isReplayLocked({
  mapId,
  mapDate,
  replayPlayerId,
  viewerPlayerId,
  now = new Date(),
}: {
  mapId: string;
  mapDate: string;
  replayPlayerId: string;
  viewerPlayerId: string;
  now?: Date;
}): boolean {
  if (viewerPlayerId === replayPlayerId) return false;

  if (isChallengeMapId(mapId)) {
    return !isChallengeReplayPublic(challengeMapIdToNumber(mapId), now);
  }

  const today = toUtcDateString(now);
  return mapDate === today;
}

function replayPagePayload({
  locked,
  playerId,
  displayName,
  mapId,
  mapDate,
  variant,
  initialState,
  moves,
  finalServerDayNumber,
}: {
  locked: boolean;
  playerId: string;
  displayName: string;
  mapId: string;
  mapDate: string;
  variant: MapVariant;
  initialState: GameState;
  moves: Move[];
  finalServerDayNumber: number;
}): ReplayPageData {
  return {
    locked,
    initialState,
    moves: locked ? [] : moves,
    mapId,
    mapDate,
    variant,
    playerId,
    displayName,
    finalServerDayNumber: locked ? 1 : finalServerDayNumber,
  };
}

async function replayPageData({
  playerId,
  viewerPlayerId,
  mapId,
  mapDate,
  initialState,
  moves,
  finalServerDayNumber,
}: {
  playerId: string;
  viewerPlayerId: string;
  mapId: string;
  mapDate: string;
  initialState: GameState;
  moves: Move[];
  finalServerDayNumber: number;
}): Promise<ReplayPageData> {
  const names = await resolveLeaderboardDisplayNames([playerId]);
  const displayName = names.get(playerId) ?? GUEST_DISPLAY_NAME;
  const locked = isReplayLocked({
    mapId,
    mapDate,
    replayPlayerId: playerId,
    viewerPlayerId,
  });
  return replayPagePayload({
    locked,
    playerId,
    displayName,
    mapId,
    mapDate,
    variant: replayVariant(mapId),
    initialState,
    moves,
    finalServerDayNumber,
  });
}

export const loadReplay = createServerFn({ method: "GET" })
  .validator(loadReplayInputSchema)
  .handler(async ({ data }): Promise<ReplayPageData | null> => {
    setRouteAction("replay_page");
    const loaded = await getReplay(data.replayId);
    if (!loaded) return null;
    const { playerId: viewerPlayerId } = await resolveRequestGameContext();

    return replayPageData({
      playerId: loaded.playerId,
      viewerPlayerId,
      mapId: loaded.mapId,
      mapDate: loaded.mapDate,
      initialState: loaded.initialState,
      moves: loaded.moves,
      finalServerDayNumber: loaded.finalServerDayNumber,
    });
  });
